import { MetaDescriptor, MetaFunction } from 'react-router'

import { getPageAppTitle } from './routing'
import { ConsumerLoaderData } from '~/portal/types/portal'
import { getContentDescription, toPlainTextExcerpt } from '~/portal/utils/shared/content'
import { isLandingPagePreviewMode } from '~/portal/utils/shared/preview'

export const getCatalogAppTitle = () => 'Swagger Catalog'

export const getBaseMeta = ({
  title,
  ogTitle,
  description,
  siteName,
  pageUrl,
  imageUrl,
  canonicalUrl,
  faviconHref,
}: {
  title: string;
  ogTitle?: string;
  description?: string;
  siteName?: string;
  pageUrl?: string;
  imageUrl?: string;
  canonicalUrl?: string;
  faviconHref?: string;
}): MetaDescriptor[] => {
  const meta: MetaDescriptor[] = [
    {
      charset: 'utf-8',
    },
    {
      name: 'viewport', content: 'width=device-width, initial-scale=1'
    },
    {
      title,
    },
  ]

  if (faviconHref) {
    meta.push({
      tagName: 'link',
      type: 'image/x-icon',
      rel: 'icon',
      href: faviconHref,
    })
  }

  if (canonicalUrl) {
    meta.push({
      tagName: 'link',
      rel: 'canonical',
      href: canonicalUrl,
    })
  }

  if (pageUrl) {
    meta.push(...[
      {
        property: 'og:title',
        content: ogTitle ?? title,
      },
      {
        property: 'og:type',
        content: 'website',
      },
      {
        property: 'og:url',
        content: pageUrl,
      },
      {
        property: 'twitter:card',
        content: imageUrl ? 'summary_large_image' : 'summary',
      },
      {
        property: 'twitter:title',
        content: ogTitle ?? title,
      },
    ])

    if (imageUrl) {
      meta.push(...[
        {
          property: 'og:image',
          content: imageUrl,
        },
        {
          property: 'twitter:image',
          content: imageUrl,
        }
      ])
    }

    if (siteName) {
      meta.push({
        property: 'og:site_name',
        content: siteName,
      })
    }

    const plainTextDescription = description ? toPlainTextExcerpt(description) : undefined

    if (plainTextDescription) {
      meta.push(...[
        {
          property: 'og:description',
          content: plainTextDescription,
        },
        {
          property: 'twitter:description',
          content: plainTextDescription,
        }
      ])
    }
  }

  return meta
}

export const getCatalogMeta = ({
  title,
  description,
  siteName,
  pageUrl,
  imageUrl,
  canonicalUrl,
  excludeFavicon = false,
}: {
  title?: string;
  description?: string;
  siteName?: string;
  pageUrl?: string;
  imageUrl?: string;
  canonicalUrl?: string;
  excludeFavicon?: boolean;
}): MetaDescriptor[] => getBaseMeta({
  title: title ?? getCatalogAppTitle(),
  description,
  siteName,
  pageUrl,
  imageUrl,
  canonicalUrl,
  faviconHref: excludeFavicon ? undefined : '/favicon_catalog.png?v=1',
})

export const getPortalMeta = ({
  title,
  ogTitle,
  description,
  siteName,
  pageUrl,
  imageUrl,
  canonicalUrl,
  platformManaged = false,
  excludeFavicon = false,
}: {
  title?: string;
  ogTitle?: string;
  description?: string;
  siteName?: string;
  pageUrl?: string;
  imageUrl?: string;
  canonicalUrl?: string;
  platformManaged?: boolean;
  excludeFavicon?: boolean;
}): MetaDescriptor[] => getBaseMeta({
  title: title ?? getPageAppTitle(platformManaged),
  ogTitle,
  description,
  siteName,
  pageUrl,
  imageUrl,
  canonicalUrl,
  faviconHref: excludeFavicon ? undefined : (platformManaged ? '/favicon_pm.ico?v=1' : '/favicon.ico?v=2'),
})

export const consumerMeta: MetaFunction = ({ loaderData, matches }) => {
  const data = loaderData as any
  const rootData: ConsumerLoaderData = matches.find(match => match.id === 'routes/_docs/_index')?.loaderData as ConsumerLoaderData
  const hasBrandedFavicons = !!rootData?.landingPageData?.faviconAttachmentId && !!rootData?.landingPageData?.faviconDarkModeAttachmentId

  return getPortalMeta({
    title: data?.metaInfo?.title ?? rootData?.portalName,
    ogTitle: data?.metaInfo?.ogTitle ?? rootData?.portalName,
    description: data?.metaInfo?.description ?? getContentDescription(rootData?.landingPageData?.description),
    siteName: rootData?.portalName,
    imageUrl: data?.landingPageData?.logoImageHref ?? rootData?.landingPageData?.logoImageHref,
    pageUrl: data?.metaInfo.pageUrl,
    canonicalUrl: data?.metaInfo.canonicalUrl,
    platformManaged: data?.apiHubPlan?.platformManaged,
    excludeFavicon: isLandingPagePreviewMode(rootData?.previewMode) || hasBrandedFavicons,
  })
}
